import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Award, CheckCircle2, Medal, Target, TrendingDown, XCircle } from 'lucide-react';
import { Button, LoadingState } from '../components/ui';
import { Html } from '../lib/htmlContent';
import { correctOptionKey, loadSavedAttemptReview, loadTestLeaderboard, optionsOf, scoreAttempt, type LeaderboardEntry, type QueRel, type ScoreSummary } from '../lib/studentTestApi';
import { pointerId } from '../lib/parse';

type RunnerState = {
  score: ScoreSummary;
  testTitle?: string;
  courseTitle?: string;
  questions: QueRel[];
  answers: [number, number][];
  examMode: boolean;
  testInfoId?: string;
} | undefined;

type RemoteResult = {
  TestTitle?: string;
  CourseTitle?: string;
  ObtainedMarks?: number;
  TotalMarks?: number;
  Percentage?: number;
  YourGrade?: string;
  TestInfoPtr?: { objectId?: string } | string;
};

type ReviewData = { questions: QueRel[]; answers: [number, number][] };

export function StudentTestResult() {
  const { resultId = '' } = useParams();
  const location = useLocation();
  const state = (location.state ?? undefined) as RunnerState;
  const [loading, setLoading] = useState(Boolean(resultId && resultId !== 'practice' && !state));
  const [remote, setRemote] = useState<RemoteResult>();
  const [review, setReview] = useState<ReviewData>();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>();
  const score: ScoreSummary | undefined = state?.score ?? (remote ? {
    obtained: Number(remote.ObtainedMarks ?? 0),
    totalMarks: Number(remote.TotalMarks ?? 0),
    percentage: Number(remote.Percentage ?? 0),
    correct: 0,
    wrong: 0,
    skipped: 0,
    grade: String(remote.YourGrade ?? 'D'),
  } : undefined);

  useEffect(() => {
    if (!resultId || resultId === 'practice' || state) return;
    let active = true;
    void (async () => {
      try {
        const saved = await loadSavedAttemptReview(resultId);
        if (active && saved) {
          setRemote(saved.result as unknown as RemoteResult);
          setReview({ questions: saved.questions, answers: [...saved.answers.entries()] });
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [resultId, state]);

  const testInfoId = state?.testInfoId ?? pointerId(remote?.TestInfoPtr);
  useEffect(() => {
    if (!testInfoId || (state && !state.examMode)) return undefined;
    let active = true;
    void loadTestLeaderboard(testInfoId).then((entries) => { if (active) setLeaderboard(entries); }).catch(() => { if (active) setLeaderboard([]); });
    return () => { active = false; };
  }, [state, testInfoId]);

  if (loading) return <LoadingState />;
  if (!score) return <div className="detail-page"><p className="detail-description">No result to show.</p><Link className="back-link" to="/tests">← All tests</Link></div>;

  const questions = state?.questions ?? review?.questions ?? [];
  const answers = state?.answers ?? review?.answers ?? [];
  const answerMap = new Map(answers);
  const detailScore = questions.length ? scoreAttempt(questions, answerMap) : undefined;
  const sectionStats = [...questions.reduce((groups, rel, index) => {
    const question = (rel.TestQuestionsPtr ?? {}) as import('../lib/studentTestApi').TestQuestion;
    const title = String(question.SubjectsPtr?.SubjectName ?? question.SubjectName ?? 'General');
    const group = groups.get(title) ?? { title, total: 0, incorrect: 0, skipped: 0 };
    group.total += 1;
    const selected = answerMap.get(index);
    const correctKey = correctOptionKey(question);
    if (selected === undefined && correctKey !== undefined) group.skipped += 1;
    else if (selected !== undefined && correctKey !== undefined && selected !== correctKey) group.incorrect += 1;
    groups.set(title, group);
    return groups;
  }, new Map<string, { title: string; total: number; incorrect: number; skipped: number }>()).values()];
  const myStanding = leaderboard?.find((entry) => entry.isMine);

  return <div className="result-page">
    <section className={`score-hero${score.percentage >= 60 ? ' pass' : ''}`}>
      <Award size={26} />
      <h1>{score.percentage >= 75 ? 'Excellent work!' : score.percentage >= 40 ? 'Test submitted' : 'Keep practising'}</h1>
      <p>{state?.testTitle ?? remote?.TestTitle ?? 'AyurGuide test'}{state?.courseTitle ?? remote?.CourseTitle ? ` · ${state?.courseTitle ?? remote?.CourseTitle}` : ''}</p>
      <div className="score-ring"><strong>{score.percentage}%</strong><span>{score.obtained}/{score.totalMarks || '—'} marks</span></div>
      <span className="score-grade">Grade {score.grade}</span>
    </section>

    <section className="score-breakdown">
      <div><CheckCircle2 size={17} /><strong>{detailScore?.correct ?? '—'}</strong><span>Correct</span></div>
      <div><XCircle size={17} /><strong>{detailScore?.wrong ?? '—'}</strong><span>Incorrect</span></div>
      <div><Target size={17} /><strong>{detailScore?.skipped ?? '—'}</strong><span>Skipped</span></div>
      <div><TrendingDown size={17} /><strong>-{(Number(score.totalMarks) - Number(score.obtained)) || 0}</strong><span>Marks lost</span></div>
    </section>

    {myStanding && <section className="detail-section leaderboard-section">
      <h2><Medal size={17} /> Your test standing</h2>
      <p className="detail-description leaderboard-standing-summary">Your best recorded attempt ranks <strong>#{myStanding.rank}</strong> of {leaderboard?.length ?? 0} learners at {myStanding.percentage}%.</p>
      <ol className="leaderboard-list" aria-label="Top test results">
        {leaderboard?.slice(0, 5).map((entry) => <li className={entry.isMine ? 'mine' : ''} key={entry.objectId}>
          <span className={`leaderboard-rank${entry.rank <= 3 ? ` top-${entry.rank}` : ''}`} aria-label={`Rank ${entry.rank}`}>#{entry.rank}</span><strong>{entry.isMine ? 'You' : entry.learnerName}</strong><span className="leaderboard-score">{entry.percentage}%</span>
        </li>)}
        {myStanding.rank > 5 && <li className="mine"><span className="leaderboard-rank" aria-label={`Rank ${myStanding.rank}`}>#{myStanding.rank}</span><strong>You</strong><span className="leaderboard-score">{myStanding.percentage}%</span></li>}
      </ol>
    </section>}

    {questions.length > 0 && <section className="detail-section section-results">
      <h2>Results by section</h2>
      <div className="section-results-list">
        {sectionStats.map((section) => <div className="section-result-row" key={section.title}>
          <strong>{section.title}</strong><span>{section.total} {section.total === 1 ? 'question' : 'questions'}</span><span>{section.incorrect} incorrect</span>{section.skipped > 0 && <span>{section.skipped} skipped</span>}
        </div>)}
      </div>
    </section>}

    {questions.length ? <section className="detail-section">
      <h2>Solutions</h2>
      <div className="solutions-list">
        {questions.map((rel, index) => {
          const question = (rel.TestQuestionsPtr ?? {}) as import('../lib/studentTestApi').TestQuestion;
          const selected = answerMap.get(index);
          const rightKey = correctOptionKey(question);
          return <article className="solution-card" key={String(question.objectId ?? index)}>
            <div className="question-number">Q{index + 1}
              {rightKey === undefined ? <em className="tag-unavailable">Not graded · answer key unavailable</em>
                : selected === undefined ? <em className="tag-skipped">Skipped</em>
                : selected === rightKey ? <em className="tag-right">Correct · +{Number(rel.PositiveMarks ?? question.Marks ?? 0)}</em>
                : <em className="tag-wrong">Incorrect{Number(rel.NegativeMarks ?? question.NegativeMarks ?? 0) ? ` · −${Number(rel.NegativeMarks ?? question.NegativeMarks)}` : ''}</em>}
            </div>
            <Html html={question.QuesTitle} className="question-text" />
            <div className="answer-options review">
              {optionsOf(question).map(({ key, html }) => (
                <div key={key} className={`${key === rightKey ? 'right' : ''}${selected === key ? (rightKey === undefined ? ' selected-ungraded' : key === rightKey ? ' selected-right' : ' selected-wrong') : ''}`}>
                  <span>{String.fromCharCode(64 + key)}</span>
                  <Html html={html} />
                  {key === rightKey && <CheckCircle2 size={15} />}
                </div>
              ))}
            </div>
            {question.SolutionInfo && <details className="solution-notes"><summary>Show explanation</summary><Html html={question.SolutionInfo} /></details>}
          </article>;
        })}
      </div>
    </section> : <section className="detail-section"><p className="detail-description">Detailed question review is unavailable for this saved result.</p></section>}

    <div className="result-actions">
      <Link to="/tests"><Button variant="soft">Back to tests</Button></Link>
    </div>
  </div>;
}
